/*
* Copyright (c) Nexign, JSC, 2019-2022.
*/
/**
 * Sagas for labelEditor component
 *
 * @author Evgenij Tyan
 * @author Aleksandr Perkhunov
 * @author Yakov Gushchin
 * @author Anton Panasenko
 * @author Ilya Babanov
 * @version 1.21.0
 * @since 1.0.0
 */

import type { SagaIterator } from '@redux-saga/types';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { manageNumberApi } from '@/main/pages/PageSubscriberId/components/ManageNumber/store';
import { checkResponseErrorWithModal, requestSaga } from '@/main/sagas/requestSaga';
import { stringFormatParams } from '@/main/utils/stringFormat';
import {
    API_HOSTNAME,
    EDIT_LABEL,
    EDIT_LABEL_SUBSCRIBER_ID,
    LABEL_CURRENT,
    LABEL_CURRENT_SUBSCRIBER_ID,
    LABEL_EDITOR,
    LABEL_EDITOR_SUBSCRIBER_ID,
} from '@modules/app/Api';
import { clearTableData } from '@modules/table/slice';
import { tableApi } from '@modules/table/store';

import {
    labelEditorCreateFailed,
    labelEditorLoadList,
    labelEditorLoadListFailed,
    labelEditorLoadListSuccessful,
    labelEditorUpdateFailed,
    labelEditorUpdateSuccessful,
} from './labelEditorActions';
import {
    LABEL_EDITOR_CREATE,
    LABEL_EDITOR_LOAD_LIST,
    LABEL_EDITOR_UPDATE,
    MapEditorTypeToLabelsApiPath,
} from './labelEditorConstants';
import { makeSelectorLabelEditorType } from './labelEditorSelectors';
import { EEditableLabel, IEditableLabel, TLabelEditorCreateAction, TLabelEditorUpdateAction } from './labelEditorTypes';

export function* labelEditorLoadListSaga(): SagaIterator {
    try {
        const type: EEditableLabel = yield select(makeSelectorLabelEditorType());
        let URL = API_HOSTNAME + MapEditorTypeToLabelsApiPath[type];
        if (type === EEditableLabel.subscriberId) {
            URL += LABEL_CURRENT_SUBSCRIBER_ID;
        } else {
            URL += LABEL_CURRENT;
        }
        const response = yield call(requestSaga, URL, 'GET');
        const labels: IEditableLabel[] = response || [];

        yield put(labelEditorLoadListSuccessful(labels));
    } catch (e: any) {
        yield put(labelEditorLoadListFailed(e));
    }
}

export function* labelEditorUpdateSaga(action: TLabelEditorUpdateAction): SagaIterator {
    const { payload } = action;
    try {
        const type: EEditableLabel = yield select(makeSelectorLabelEditorType());
        let URL = API_HOSTNAME + MapEditorTypeToLabelsApiPath[type];
        if (type === EEditableLabel.subscriberId) {
            URL += stringFormatParams(LABEL_EDITOR_SUBSCRIBER_ID, { entityType: EEditableLabel.mobile });
        } else {
            URL += LABEL_EDITOR;
        }

        yield call(requestSaga, URL, 'PUT', { labels: payload });

        if (type !== EEditableLabel.subscriberId) {
            // TODO: remove clear table state (uses for full table re-render)
            yield put(clearTableData());
            yield put(tableApi.util.invalidateTags(['TableMeta', 'TableList', 'TableLabels']));
        }
        if (type === EEditableLabel.subscriberId) {
            yield put(manageNumberApi.util.invalidateTags(['ManageNumberMeta']));
        }
        yield put(labelEditorUpdateSuccessful());
    } catch (e: any) {
        yield put(labelEditorUpdateFailed(e));
        yield call(checkResponseErrorWithModal, e);
        yield put(tableApi.util.invalidateTags(['TableMeta', 'TableList', 'TableLabels']));
    }
}

export function* labelEditorCreateSaga(action: TLabelEditorCreateAction): SagaIterator {

    const payload = action.payload;

    try {
        const type: EEditableLabel = yield select(makeSelectorLabelEditorType());
        let URL = API_HOSTNAME + MapEditorTypeToLabelsApiPath[type];
        if (type === EEditableLabel.subscriberId) {
            URL += (EDIT_LABEL_SUBSCRIBER_ID + '?name=' + encodeURIComponent(payload));
        } else {
            URL += (EDIT_LABEL + '?name=' + encodeURIComponent(payload));
        }
        yield call(requestSaga, URL, 'POST');

        yield put(labelEditorLoadList({
            clearList: false,
        }));
    } catch (e: any) {
        yield put(labelEditorCreateFailed(e));
        yield call(checkResponseErrorWithModal, e);
    }
}

export function* labelEditorSaga(): SagaIterator {
    yield all([
        takeEvery(LABEL_EDITOR_CREATE, labelEditorCreateSaga),
        takeEvery(LABEL_EDITOR_LOAD_LIST, labelEditorLoadListSaga),
        takeEvery(LABEL_EDITOR_UPDATE, labelEditorUpdateSaga),
    ]);
}
